<!-- @format -->

<template>
	<div
		ref="root"
		:class="[
			'core-sortable-list',
			{ 'core-sortable-list--empty': modelValue.length === 0 },
		]"
	>
		<slot />
	</div>
</template>

<script>
import { createSortableList } from '@/helpers/sortable.js'

const TOP_LEVEL_KEYS = ['disabled', 'group', 'handle', 'animation', 'ghostClass', 'dragClass']

export default {
	name: 'CoreSortableList',

	props: {
		modelValue: {
			type: Array,
			required: true,
		},
		group: {
			type: [String, Object],
			default: null,
		},
		handle: {
			type: String,
			default: null,
		},
		animation: {
			type: Number,
			default: 150,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		ghostClass: {
			type: String,
			default: null,
		},
		dragClass: {
			type: String,
			default: null,
		},
		options: {
			type: Object,
			default: () => ({}),
		},
	},

	emits: ['update:modelValue'],

	mounted() {
		const sortableProps = {}
		if (this.group !== null) sortableProps.group = this.group
		if (this.handle !== null) sortableProps.handle = this.handle
		if (this.ghostClass !== null) sortableProps.ghostClass = this.ghostClass
		if (this.dragClass !== null) sortableProps.dragClass = this.dragClass

		this._sortableHandle = createSortableList(this.$refs.root, {
			getItems: () => this.modelValue,
			setItems: (next) => this.$emit('update:modelValue', next),
			...this.options,
			animation: this.animation,
			disabled: this.disabled,
			...sortableProps,
		})
	},

	beforeUnmount() {
		if (this._sortableHandle) {
			this._sortableHandle.destroy()
			this._sortableHandle = null
		}
	},

	watch: {
		disabled(v) {
			this.applyOption('disabled', v)
		},
		group(v) {
			this.applyOption('group', v)
		},
		handle(v) {
			this.applyOption('handle', v)
		},
		animation(v) {
			this.applyOption('animation', v)
		},
		ghostClass(v) {
			this.applyOption('ghostClass', v)
		},
		dragClass(v) {
			this.applyOption('dragClass', v)
		},
		options: {
			deep: true,
			handler(v) {
				if (!v) return
				Object.entries(v).forEach(([k, val]) => {
					if (TOP_LEVEL_KEYS.includes(k)) return
					this.applyOption(k, val)
				})
			},
		},
	},

	methods: {
		applyOption(name, value) {
			if (this._sortableHandle && this._sortableHandle.sortable) {
				this._sortableHandle.sortable.option(name, value)
			}
		},
	},
}
</script>

<style scoped>
.core-sortable-list {
	display: flex;
	flex-wrap: wrap;
	gap: 0.5rem;
	min-height: 3rem;
	align-items: flex-start;
}

.core-sortable-list--empty {
	align-items: center;
	justify-content: center;
}
</style>
